import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Utensils, Heart, Store, Bot, CalendarCheck, MessageSquare, Check } from 'lucide-react';
import { SECTORS } from '../constants';

const SECTOR_ICONS: Record<string, React.ReactNode> = {
  hosteleria: <Utensils size={16} />,
  salud: <Heart size={16} />,
  comercio: <Store size={16} />,
};

const SOLUTION_ICONS: Record<string, React.ReactNode[]> = {
  hosteleria: [<Bot size={26} />, <CalendarCheck size={26} />, <MessageSquare size={26} />],
  salud: [<CalendarCheck size={26} />, <Bot size={26} />, <Heart size={26} />],
  comercio: [<Bot size={26} />, <MessageSquare size={26} />, <CalendarCheck size={26} />],
};

const SOLUTION_IMAGES: Record<string, string[]> = {
  hosteleria: [
    'https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/460537/pexels-photo-460537.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/5081418/pexels-photo-5081418.jpeg?auto=compress&cs=tinysrgb&w=800',
  ],
  salud: [
    'https://images.pexels.com/photos/3985163/pexels-photo-3985163.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/4046316/pexels-photo-4046316.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/3985163/pexels-photo-3985163.jpeg?auto=compress&cs=tinysrgb&w=800',
  ],
  comercio: [
    'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=800',
    'https://images.pexels.com/photos/3184293/pexels-photo-3184293.jpeg?auto=compress&cs=tinysrgb&w=800',
  ],
};

export const SectorSection: React.FC = () => {
  const [activeSector, setActiveSector] = useState(SECTORS[0].id);
  const activeData = SECTORS.find((s) => s.id === activeSector) || SECTORS[0];
  const solutionImages = SOLUTION_IMAGES[activeSector] || [];
  const solutionIcons = SOLUTION_ICONS[activeSector] || [];

  return (
    <section id="hosteleria" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-emerald-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Mobile Tabs */}
        <div className="md:hidden flex flex-wrap justify-center gap-3 pb-2 mb-8">
          {SECTORS.map((sector) => (
            <button
              key={sector.id}
              onClick={() => setActiveSector(sector.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold transition-all duration-300 border whitespace-nowrap ${
                activeSector === sector.id
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {SECTOR_ICONS[sector.id]}
              {sector.label}
            </button>
          ))}
        </div>

        {/* Desktop Tabs - Pill */}
        <div className="hidden md:flex justify-center mb-12">
          <div className="p-1.5 rounded-full flex items-center gap-1" style={{ background: 'rgba(0,0,0,0.04)' }}>
            {SECTORS.map((sector) => (
              <button
                key={sector.id}
                onClick={() => setActiveSector(sector.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${
                  activeSector === sector.id
                    ? 'bg-slate-900 text-white shadow-lg'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {SECTOR_ICONS[sector.id]}
                {sector.label}
              </button>
            ))}
          </div>
        </div>

        {/* Active Sector Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSector}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Tagline */}
            <div className="text-center mb-12">
              <p className="text-lg text-slate-500 max-w-2xl mx-auto font-light leading-relaxed">
                {activeData.tagline}
              </p>
            </div>

            {/* Solutions Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {activeData.solutions.map((solution, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl flex flex-col h-full group transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                  style={{
                    background: '#ffffff',
                    border: '1px solid rgba(0,0,0,0.07)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  }}
                >
                  {/* Image */}
                  <div className="h-48 overflow-hidden relative bg-slate-800 flex-shrink-0">
                    <img
                      src={solutionImages[i] || ''}
                      alt={solution.title}
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent" />
                    {/* Badge on image */}
                    {solution.badge && (
                      <div
                        className="absolute bottom-3 left-3 text-xs font-bold px-3 py-1 rounded-full text-white"
                        style={{ background: 'rgba(60, 179, 113, 0.85)', backdropFilter: 'blur(8px)' }}
                      >
                        {solution.badge}
                      </div>
                    )}
                  </div>

                  {/* Floating Icon */}
                  <div className="relative px-6">
                    <div
                      className="absolute -top-5 right-5 w-11 h-11 rounded-xl flex items-center justify-center z-20 shadow-md group-hover:scale-110 transition-transform duration-300"
                      style={{
                        background: 'linear-gradient(135deg, #3CB371, #1a8a52)',
                        boxShadow: '0 4px 15px rgba(60,179,113,0.35)',
                      }}
                    >
                      <div className="text-white">{solutionIcons[i]}</div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="mt-4">
                      <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors leading-snug">
                        {solution.title}
                      </h3>
                      <p className="text-slate-500 text-sm flex-grow mb-4 leading-relaxed font-light">
                        {solution.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Wow List */}
            <div className="max-w-3xl mx-auto mb-12">
              <h4 className="text-lg font-bold text-slate-900 mb-6 text-center">
                TODO ESTO ES POSIBLE EN TU NEGOCIO
              </h4>
              <ul className="space-y-4">
                {activeData.wowList.map((item, i) => (
                  <li key={i} className="flex items-start gap-4 text-slate-700">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: 'rgba(60,179,113,0.15)' }}
                    >
                      <Check size={14} className="text-emerald-600" strokeWidth={3} />
                    </div>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <button
                type="button"
                onClick={() => document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 bg-primary text-white hover:opacity-90 shadow-lg shadow-primary/30 focus:ring-primary text-base px-8 py-3 rounded-full shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #3CB371, #1a8a52)',
                  boxShadow: '0 4px 20px rgba(60,179,113,0.3)',
                }}
              >
                Ver precios
              </button>
              <p className="text-sm text-slate-500 mt-3">
                Sin compromiso. Garantía de devolución 30 días.
              </p>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
